import { Box, Divider, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

export interface LegendItem {
  key: string;
  color: string;
  label: string;
  // Items sharing the same section render together, separated from other
  // sections by a divider — keeps unrelated groups (e.g. grade categories)
  // from visually blending into the main map legend.
  section?: string;
  sectionLabel?: string;
}

interface RoadsImporterLegendProps {
  items: LegendItem[];
}

const renderItem = (item: LegendItem): JSX.Element => (
  <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: item.color,
        flexShrink: 0,
      }}
    />
    <Typography
      variant="caption"
      sx={{ color: 'text.primary', lineHeight: 1.4 }}
    >
      {item.label}
    </Typography>
  </Box>
);

export function RoadsImporterLegend({
  items,
}: RoadsImporterLegendProps): JSX.Element | null {
  if (items.length === 0) return null;

  // Group consecutive items by section, preserving overall order — items
  // without a section fall into a single unnamed default group.
  const groups: Array<{
    section: string;
    sectionLabel?: string;
    items: LegendItem[];
  }> = [];
  items.forEach((item) => {
    const section = item.section ?? '__default';
    const last = groups[groups.length - 1];
    if (last?.section === section) {
      last.items.push(item);
    } else {
      groups.push({
        section,
        sectionLabel: item.sectionLabel,
        items: [item],
      });
    }
  });

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 12,
        right: 12,
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: (th) => alpha(th.palette.background.paper, 0.92),
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 2,
      }}
    >
      {groups.map((group, idx) => (
        <Box
          key={group.section}
          sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
        >
          {idx > 0 && <Divider sx={{ my: 0.5 }} />}
          {group.sectionLabel && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 600 }}
            >
              {group.sectionLabel}
            </Typography>
          )}
          {group.items.map(renderItem)}
        </Box>
      ))}
    </Box>
  );
}
